<template>
  <v-app>
    <v-main class="pa-4">
      <!-- === SECCIÓN: GESTIÓN DE USUARIOS === -->
      <v-card
        elevation="4"
        class="mb-8"
        outlined
        :style="{ borderRadius: '12px' }"
      >
        <v-card-title class="text-h5 font-weight-bold primary--text">
          <v-icon color="primary" left>mdi-account-group</v-icon>
          Gestión de Usuarios
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            rounded
            @click="openCreateDialog"
          >
            <v-icon left>mdi-plus</v-icon>
            Nuevo usuario
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-data-table
          :headers="headers"
          :items="users"
          :loading="loading"
          class="elevation-0"
        >
          <template v-slot:item.role_name="{ item }">
            <v-chip small color="primary" text-color="white">
              {{ item.role_name }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="primary" @click="openEditDialog(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <!-- === SECCIÓN: HISTORIAL DE ACCIONES === -->
      <v-card
        elevation="4"
        class="mb-8"
        outlined
        :style="{ borderRadius: '12px' }"
      >
        <v-card-title class="text-h5 font-weight-bold primary--text">
          <v-icon color="primary" left>mdi-history</v-icon>
          Historial de Acciones
          <v-spacer></v-spacer>

          <!-- Rango de fechas (responsive) -->
          <v-row no-gutters class="d-flex align-center" style="max-width: 420px;">
            <v-col cols="12" sm="6" class="pr-1">
              <v-menu v-model="menuStart" :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateStart"
                    label="Desde"
                    prepend-inner-icon="mdi-calendar-start"
                    readonly
                    dense
                    outlined
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    class="text-center"
                    style="min-width: 120px"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateStart"
                  @input="menuStart = false; loadHistory()"
                  color="primary"
                  full-width
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" class="pl-1 mt-2 mt-sm-0">
              <v-menu v-model="menuEnd" :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateEnd"
                    label="Hasta"
                    prepend-inner-icon="mdi-calendar-end"
                    readonly
                    dense
                    outlined
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    class="text-center"
                    style="min-width: 120px"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateEnd"
                  @input="menuEnd = false; loadHistory()"
                  color="primary"
                  full-width
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider></v-divider>

        <v-data-table
          :headers="historyHeaders"
          :items="history"
          :loading="loadingHistory"
          class="elevation-0"
          :items-per-page="-1"
        >
          <template v-slot:item.created_at="{ item }">
            <span class="text--secondary">{{ formatLocalDate(item.created_at) }}</span>
          </template>
          <template v-slot:item.description="{ item }">
            <span>{{ (item.description || '').toUpperCase() }}</span>
          </template>
          <template v-slot:no-data>
            <v-alert
              color="info"
              icon="mdi-information"
              class="ma-4"
              outlined
              text
            >
              No hay registros en el período seleccionado.
            </v-alert>
          </template>
        </v-data-table>
      </v-card>

      <!-- Diálogo: Crear / Editar -->
      <v-dialog v-model="dialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6 font-weight-bold">
            {{ editingUser ? 'Editar usuario' : 'Crear usuario' }}
          </v-card-title>
          <v-card-text>
            <v-form ref="form">
              <v-text-field
                v-model="formUser.username"
                label="Usuario *"
                prepend-inner-icon="mdi-account"
                :rules="[v => !!v || 'El usuario es obligatorio']"
                outlined
                dense
              ></v-text-field>
              <v-text-field
                v-model="formUser.email"
                label="Email *"
                prepend-inner-icon="mdi-email"
                :rules="[v => !!v || 'El email es obligatorio', v => /.+@.+\..+/.test(v) || 'Email inválido']"
                outlined
                dense
              ></v-text-field>
              <v-text-field
                v-model="formUser.role_name"
                label="Rol *"
                prepend-inner-icon="mdi-shield-account"
                :rules="[v => !!v || 'El rol es obligatorio']"
                outlined
                dense
              ></v-text-field>
              <v-text-field
                v-if="!editingUser"
                v-model="formUser.password"
                label="Contraseña *"
                prepend-inner-icon="mdi-lock"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'"
                @click:append="showPassword = !showPassword"
                :rules="[v => !!v || 'La contraseña es obligatoria']"
                outlined
                dense
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialog = false">Cancelar</v-btn>
            <v-btn color="primary" @click="saveUser">Guardar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Diálogo de notificación -->
      <v-dialog v-model="alertDialog" max-width="400px">
        <v-alert :type="alertType" elevation="4">
          {{ alertMessage }}
          <v-btn
            text
            @click="alertDialog = false"
            style="position: absolute; top: 8px; right: 8px"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-alert>
      </v-dialog>
    </v-main>
  </v-app>
</template>

<script>
import { mapActions } from "vuex";
import moment from 'moment-timezone';

export default {
  name: "UserManagement",
  data() {
    const today = moment().format("YYYY-MM-DD");
    return {
      loading: false,
      users: [],
      dialog: false,
      alertDialog: false,
      alertMessage: "",
      alertType: "success",
      editingUser: false,
      showPassword: false,
      formUser: {
        username: "",
        email: "",
        role_name: "",
        password: "",
      },
      headers: [
        { text: "Usuario", value: "username" },
        { text: "Email", value: "email" },
        { text: "Rol", value: "role_name" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      // Historial
      history: [],
      loadingHistory: false,
      menuStart: false,
      menuEnd: false,
      dateStart: today,
      dateEnd: today,
      historyHeaders: [
        { text: "Usuario", value: "username", width: "20%" },
        { text: "Descripción", value: "description", width: "60%" },
        { text: "Fecha y hora", value: "created_at", width: "20%" }
      ],
    };
  },
  async created() {
    await this.loadUsers();
    await this.loadHistory();
  },
  methods: {
    ...mapActions(["getData", "postData", "putData"]),

    async loadUsers() {
      this.loading = true;
      try {
        const response = await this.getData({ path: "auth/users" });
        this.users = response.users || response;
      } catch (error) {
        console.error("Error al cargar usuarios:", error);
        this.showAlert("Error al cargar los usuarios", "error");
      } finally {
        this.loading = false;
      }
    },

    async loadHistory() {
      if (moment(this.dateEnd).isBefore(this.dateStart)) {
        this.showAlert("La fecha 'Hasta' no puede ser anterior a 'Desde'", "error");
        return;
      }

      this.loadingHistory = true;
      try {
        const start = moment.tz(this.dateStart, "Europe/Madrid").startOf('day');
        const end = moment.tz(this.dateEnd, "Europe/Madrid").endOf('day');

        const initTimestamp = start.toISOString();
        const endTimestamp = end.toISOString();

        const response = await this.getData({
          path: "user",
          params: { initTimestamp, endTimestamp }
        });
        this.history = response.history || [];
      } catch (error) {
        console.error("Error al cargar historial:", error);
        this.showAlert("Error al cargar el historial", "error");
      } finally {
        this.loadingHistory = false;
      }
    },

    openCreateDialog() {
      this.editingUser = false;
      this.formUser = { username: "", email: "", role_name: "", password: "" };
      this.showPassword = false;
      this.dialog = true;
    },

    openEditDialog(user) {
      this.editingUser = true;
      this.formUser = { ...user };
      this.dialog = true;
    },

    async saveUser() {
      const form = this.$refs.form;
      if (!form.validate()) return;

      try {
        if (this.editingUser) {
          await this.putData({
            path: `auth/user/${this.formUser.id}`,
            body: this.formUser,
          });
          this.showAlert("Usuario actualizado correctamente", "success");
        } else {
          await this.postData({
            path: "auth/user",
            body: this.formUser,
          });
          this.showAlert("Usuario creado correctamente", "success");
        }
        await this.loadUsers();
        this.dialog = false;
      } catch (error) {
        console.error("Error al guardar usuario:", error);
        this.showAlert("Error al guardar el usuario", "error");
      }
    },

    formatLocalDate(utcDateString) {
      if (!utcDateString) return "";
      return moment.utc(utcDateString).tz("Europe/Madrid").format("DD/MM/YYYY HH:mm:ss");
    },

    showAlert(message, type) {
      this.alertMessage = message;
      this.alertType = type;
      this.alertDialog = true;
    },
  },
};
</script>

<style scoped>
.v-card--outlined {
  border: 1px solid rgba(0, 0, 0, 0.1);
}
</style>